import { gql, useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames/bind';
import MouseTrap from 'mousetrap';
import { type SyntheticEvent, useEffect, useState } from 'react';
import {
  BirthdayNotif,
  CalendarNotif,
  ChevronDown,
  DocumentNotif,
  Information,
  PTOApproveNotif,
  TimesheetWarningNotif,
  UserClock,
  Vault,
  WarningCircle,
  WarningNotifOrange,
} from '../../assets/icons/Icons';
import { useConfig, useUser } from '../../context/PortalContext/PortalContext';
import useClickOutside from '../../hooks/useClickOutside/useClickOutside';
import { NO_PROFILE_IMG_URL, NotificationTypes } from '../../utils/constants';
import Button from '../Button/Button';
import NotificationAlert from '../NotificationAlert/NotificationAlert';
import Styles from './avatar.module.scss';
import ContainerTriangleSVG from './containerTriangle.svg';

const cx = classNames.bind(Styles);

const GET_NOTIFICATIONS = gql`
  query AvatarUserNotifications {
    notifications(version: 9, read: false) {
      notifications {
        id
        type
        priority
        title
        text
        action
        data
        date
        subtype
      }
    }
  }
`;

const READ_NOTIFICATIONS = gql`
  mutation ($id: Int) {
    readNotification(id: $id) {
      id
      type
      priority
      title
      text
      action
      data
      date
      subtype
    }
  }
`;

interface ManualNotification {
  icon: string;
  message: string;
  date: Date | string;
}

interface Props {
  /* Should only be used for testing purposes. The notifications to display; an array containing arrays of the format [icon, message, date]. */
  manualNotifications?: ManualNotification[];
  className?: string;
  showBorder?: boolean;
}

const Avatar = ({ manualNotifications, className, showBorder = false }: Props) => {
  const context = useUser();
  const config = useConfig();
  if (!context?.details) {
    return null;
  }
  const { id, nameFirst, nameLast, jobTitle, email, photoUrl } = context.details;

  // Get urls based on environment
  const { directory, logoutUrl } = config?.urls || { directory: '', logoutUrl: '' };

  // Construct profile url
  const viewProfileLink = `${directory}/profile/${id}/${nameFirst}-${nameLast}`;

  // Construct logout url
  const prevLocation = encodeURI(window.location.href);
  const logoutLink = logoutUrl ? encodeURI(`${logoutUrl}?returnUrl=${prevLocation}`) : logoutUrl;

  const photoUrlMedium = photoUrl?.replace(`50.jpg`, '250.jpg');

  // Fall back to the placeholder if a (potentially expired) signed S3 URL fails to load. Guarded so
  // the fallback failing can't re-trigger onError in a loop. See KGP-1164.
  const handleImageError = (e: SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.src !== NO_PROFILE_IMG_URL) {
      e.currentTarget.src = NO_PROFILE_IMG_URL;
    }
  };

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    MouseTrap.bind('esc', () => {
      setIsOpen(false);
    });

    MouseTrap.bind(['command+j', 'ctrl+j'], (e) => {
      if (e?.preventDefault) {
        e.preventDefault();
      }
      setIsOpen((show) => !show);
    });

    return () => {
      MouseTrap.unbind(['command+j', 'ctrl+j']);
      MouseTrap.unbind('esc');
    };
  }, []);

  const ref = useClickOutside<HTMLDivElement>(() => setIsOpen(false), isOpen);
  const { data } = useQuery(GET_NOTIFICATIONS);
  const notifications = data
    ? [...data.notifications.notifications, ...(manualNotifications || [])]
    : [...(manualNotifications || [])];

  const [readNotif] = useMutation(READ_NOTIFICATIONS, {
    refetchQueries: [GET_NOTIFICATIONS],
    awaitRefetchQueries: true,
  });

  const getIcon = (notification) => {
    switch (notification.type) {
      case NotificationTypes.TIMESHEET:
        return TimesheetWarningNotif;
      case NotificationTypes.TIMESHEET_MANAGER:
        return TimesheetWarningNotif;
      case NotificationTypes.DYNAMIC:
        return notification.subtype === 'vault-inbox' ? Vault : CalendarNotif;
      case NotificationTypes.UPCOMING_REVIEW:
        return UserClock;
      case NotificationTypes.BIRTHDAY:
        return BirthdayNotif;
      case NotificationTypes.TRAVEL_OPS:
      case NotificationTypes.TRAVEL_MANAGER:
      case NotificationTypes.PTO_MANAGER:
        return PTOApproveNotif;
      case NotificationTypes.PROJECTS_APPROVAL_COUNT:
      case NotificationTypes.CLIENTS_APPROVAL_COUNT:
        return Information;
      case NotificationTypes.ACTION_ITEM:
        return WarningCircle;
      case NotificationTypes.IT_COMPLIANCE:
        return WarningCircle;
      case NotificationTypes.WORK_HISTORY:
        return DocumentNotif;
      default:
        return CalendarNotif;
    }
  };

  const readNotification = (id, e) => {
    e.preventDefault();
    readNotif({ variables: { id: id } });
  };

  return (
    <div className={cx('avatar', className)} ref={ref} data-testid={isOpen ? 'avatar-open' : 'avatar'}>
      <div className={cx('avatar-image-container')}>
        {/* Link only renders for mobile and must be outside of button element for accessibility */}
        <a href={viewProfileLink} className={cx('avatar-image-link')}>
          <img
            src={photoUrlMedium}
            className={cx('avatar-image')}
            title='profile-photo'
            alt={`${nameFirst} ${nameLast}`}
            onError={handleImageError}
          />
        </a>
        <button
          className={cx('avatar-image-button', { 'avatar-image-button--no-notif': notifications.length === 0 })}
          role='button'
          onClick={() => setIsOpen(!isOpen)}
          aria-disabled={notifications.length === 0}
          data-testid='avatar-menu-button'>
          <div className={cx('avatar-image-group') + ' show-md'}>
            <img
              src={photoUrlMedium}
              className={cx('avatar-image', { 'avatar-image--bordered': showBorder })}
              title='profile-photo'
              alt={`${nameFirst} ${nameLast}`}
              onError={handleImageError}
            />
            {notifications.length > 0 ? <span className={cx('warning-icon')}> {WarningNotifOrange} </span> : null}
          </div>
          <div className={cx('avatar-info', 'avatar-info--mobile')}>
            <p className={cx('avatar-name')}>{`${nameFirst} ${nameLast}`}</p>
            <p className={cx('avatar-title')}>{jobTitle}</p>
          </div>
          <span className={cx('chevron', { 'chevron--up': isOpen })}>{ChevronDown}</span>
        </button>
      </div>
      <div
        className={cx('avatar-menu-container', {
          'avatar-menu-container--open': isOpen,
        })}>
        <img src={ContainerTriangleSVG} role='presentation' className={cx('triangle')} />
        <div
          className={cx('avatar-menu')}
          role='dialog'
          aria-label='Profile Menu'
          aria-labelledby='Avatar'
          aria-describedby={`${nameFirst} ${nameLast}`}>
          <div className={cx('avatar-content')}>
            <img
              src={photoUrlMedium}
              className={cx('avatar-photo')}
              alt={`${nameFirst} ${nameLast}`}
              onError={handleImageError}
            />
            <div className={cx('avatar-info')}>
              <p className={cx('avatar-name')} id='Name'>{`${nameFirst} ${nameLast}`}</p>
              <p className={cx('avatar-title')} id='JobTitle'>
                {jobTitle}
              </p>
              <a href={`mailto:${email}`} className={cx('avatar-email')} id='Email'>
                {email}
              </a>
            </div>
          </div>
          {notifications.length > 0 && (
            <div className={cx('avatar-notifications')}>
              {notifications.map((notification) => (
                <NotificationAlert
                  key={notification.title}
                  icon={getIcon(notification)}
                  text={notification.title}
                  subText={notification.text}
                  link={notification.action}
                  onDismiss={
                    notification.type === NotificationTypes.DYNAMIC
                      ? (e) => readNotification(notification.id, e)
                      : undefined
                  }
                />
              ))}
            </div>
          )}
          <div
            className={
              notifications.length > 0 ? cx('avatar-footer') : cx('avatar-footer', 'avatar-footer--add-border')
            }>
            <Button
              aria-label='View Profile'
              size='small'
              type='secondary'
              fullWidth={true}
              onClick={() => window.open(viewProfileLink, '_self')}>
              View Profile
            </Button>
            <Button
              aria-label='Sign Out'
              size='small'
              type='primary'
              fullWidth={true}
              onClick={() => window.open(logoutLink, '_self')}>
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Avatar;
